import { useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';
import { fetcher } from 'thera-swr/fetcher';

import { IBulkPayResponse } from 'services/invoiceApi/invoicesApi';
import { IInvoiceInfo } from 'types';
import { isInvoiceDueOrWithinAllowedWindow, isInvoiceOpen, isInvoiceUnapproved } from 'utils/invoice';
import { formatErrorMessage } from 'utils/error';
import { globalFetcherSettings } from 'lib/constants';

const apiBaseUrl = process.env.NEXT_PUBLIC_BACKEND_URL;
const path = '/invoices/bulk-pay';
const apiUrl = apiBaseUrl + path;

/**
 * Decides whether a failed preview fetch is worth retrying. Only server-side
 * failures (5xx) are transient. A 4xx means the request itself is terminal for
 * this ID set — e.g. previewing an invoice that was just deleted answers 403
 * "does not have permission perm_view"; replaying it only delays a stale-data
 * error toast. A missing status points to a non-HTTP failure, treated as transient.
 */
function isRetryableError(error: { status?: number }): boolean {
  const status = error?.status;
  if (typeof status !== 'number') return true;
  return status >= 500;
}

/**
 * Fetches bulk-pay preview data for one invoice set and returns a copy of that
 * set with each invoice enriched with its rate-converted payment amounts, plus
 * the aggregate amounts. `enabled` gates the network call (pass false to skip).
 * Used by useInvoices and by pages that need preview data for a custom-filtered invoice subset.
 */
export default function useInvoicePreview(
  companyID: string,
  invoices: IInvoiceInfo[],
  enabled: boolean,
  /** When set, openAmount is restricted to invoices due-or-within-window — matches getOpenInvoiceCounts so it aligns with the actionable count. totalOutstandingAmount is unaffected and always sums every open+unapproved invoice. */
  companyInvoiceAvailabilityDaysCount?: number,
  /** Optional per-invoice override resolver. Takes precedence over the company-level count. */
  getDaysForInvoice?: (invoiceInfo: IInvoiceInfo) => number | undefined,
) {
  const invoiceIds = useMemo(
    () =>
      (invoices || []).filter((invoiceInfo) => isInvoiceOpen(invoiceInfo)).map((invoiceInfo) => invoiceInfo.invoice.id),
    [invoices],
  );

  const [hasExhaustedRetryCount, setHasExhaustedRetryCount] = useState(false);
  const [invoicesWithPreviewData, setInvoicesWithPreviewData] = useState<IInvoiceInfo[]>();
  const [isCalculatingStats, setIsCalculatingStats] = useState(false);
  const [amountStats, setAmountStats] = useState({
    openAmount: 0,
    totalOutstandingAmount: 0,
    totalSubscriptionAmount: 0,
    totalAmount: 0,
    totalAmountWithSubscription: 0,
  });

  const {
    data: fetchPreviewRes,
    error,
    mutate,
    isLoading,
  } = useSWR<IBulkPayResponse>(
    () => (companyID && invoiceIds.length > 0 && enabled ? [path, JSON.stringify(invoiceIds)] : false),
    fetcher(apiUrl, 'POST', { invoiceIds, preview: true }, (fetchError) => {
      // Tag only the known stale-list signatures (the invoice set raced a concurrent
      // pay/void/delete) so error-tracking monitors can exclude them. Any other 4xx —
      // e.g. a malformed-request regression — stays untagged and keeps alerting.
      const status = (fetchError as { httpStatus?: number })?.httpStatus;
      const message = formatErrorMessage(fetchError, '')?.toLowerCase() ?? '';
      const isStaleInvoiceSetError =
        (status === 400 && message.includes('open status')) ||
        (status === 403 && message.includes('does not have permission'));
      if (isStaleInvoiceSetError) {
        return { source: 'useInvoicePreview', tags: { thera_error_type: 'expected_error' } };
      }
      return undefined;
    }),
    {
      ...globalFetcherSettings,
      onErrorRetry: (fetchError, __, ___, revalidate, { retryCount }) => {
        // A 4xx will never succeed on retry. Stop immediately and clear any prior
        // preview data so page-loading gates resolve; leave hasExhaustedRetryCount
        // false so no error toast fires — the list refetch re-keys to a valid set.
        if (!isRetryableError(fetchError)) {
          setInvoicesWithPreviewData([]);
          return;
        }
        if (retryCount <= 1) {
          setHasExhaustedRetryCount(false);
        }
        if (retryCount > globalFetcherSettings?.errorRetryCount) {
          setInvoicesWithPreviewData([]);
          setHasExhaustedRetryCount(true);
          return;
        }
        setTimeout(() => revalidate({ retryCount }), 2000);
      },
      onSuccess: () => {
        setHasExhaustedRetryCount(false);
      },
    },
  );

  // When the fetch is intentionally skipped (no invoices, disabled, or no company),
  // resolve invoicesWithPreviewData to [] so callers gating on it (e.g. page-loading
  // guards using `!invoicesWithPreview`) don't hang on a never-resolved fetch.
  // When disabled temporarily (e.g. during post-payment revalidation gating), preserve
  // existing data to avoid flashing "No invoices" before navigation completes.
  useEffect(() => {
    if (!companyID || invoiceIds.length === 0) {
      setInvoicesWithPreviewData([]);
    } else if (!enabled) {
      setInvoicesWithPreviewData((prev) => prev ?? []);
    }
  }, [companyID, enabled, invoiceIds.length]);

  // Enrich a copy of the invoice set with rate-converted preview amounts. While a new invoice set is
  // still being previewed, fetchPreviewRes is the previous set's response (keepPreviousData), so keep
  // the last fully enriched list rather than rendering the new rows with blank amounts.
  useEffect(() => {
    if (!invoices || !fetchPreviewRes || !enabled || isLoading) return;

    setIsCalculatingStats(true);
    const { successInvoices } = fetchPreviewRes;
    const invoicesCopy: IInvoiceInfo[] = JSON.parse(JSON.stringify(invoices));

    let totalActualPaymentAmount = 0;
    let totalActualPaymentAmountWithSubscription = 0;
    let theraSubscriptionTotal = 0;
    let totalOpenAmount = 0;
    let totalOutstandingAmount = 0;
    for (const invoicePreview of successInvoices || []) {
      const invoiceInfo = invoicesCopy.find((info) => info.invoice.id === invoicePreview.id);
      if (!invoiceInfo) continue;

      invoiceInfo.invoice.actualPaymentAmount = invoicePreview.actualPaymentAmount;
      invoiceInfo.invoice.actualPaymentCurrency = invoicePreview.actualPaymentCurrency;
      invoiceInfo.invoice.totalActualPaymentAmount = invoicePreview.totalActualPaymentAmount;
      theraSubscriptionTotal += invoicePreview.subscriptionChargeAmount ?? 0;
      totalActualPaymentAmount += invoicePreview.actualPaymentAmount;
      totalActualPaymentAmountWithSubscription += invoicePreview.totalActualPaymentAmount ?? 0;

      if (isInvoiceUnapproved(invoiceInfo) && isInvoiceOpen(invoiceInfo)) {
        totalOutstandingAmount += invoiceInfo.invoice.actualPaymentAmount;
        const allowedDays = getDaysForInvoice?.(invoiceInfo) ?? companyInvoiceAvailabilityDaysCount;
        if (isInvoiceDueOrWithinAllowedWindow(invoiceInfo.invoice.paymentDueDate, allowedDays)) {
          totalOpenAmount += invoiceInfo.invoice.actualPaymentAmount;
        }
      }
    }

    setInvoicesWithPreviewData(invoicesCopy);
    setAmountStats({
      openAmount: totalOpenAmount,
      totalOutstandingAmount,
      totalSubscriptionAmount: theraSubscriptionTotal,
      totalAmount: totalActualPaymentAmount,
      totalAmountWithSubscription: totalActualPaymentAmountWithSubscription,
    });
    setIsCalculatingStats(false);
    // invoiceIds intentionally excluded — the effect reads invoices directly; adding invoiceIds would double-process
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fetchPreviewRes,
    isLoading,
    companyID,
    enabled,
    invoices,
    companyInvoiceAvailabilityDaysCount,
    getDaysForInvoice,
  ]);

  const errorWithHasErrorAfterAllRetries = error
    ? { ...error, hasErrorAfterAllRetries: Boolean(error?.name && hasExhaustedRetryCount) }
    : null;

  return {
    invoicesWithPreviewData,
    mutateInvoicePreview: mutate,
    openAmount: enabled ? amountStats.openAmount || 0 : undefined,
    totalOutstandingAmount: enabled ? amountStats.totalOutstandingAmount || 0 : undefined,
    totalSubscriptionAmount: enabled ? amountStats.totalSubscriptionAmount || 0 : undefined,
    totalAmount: enabled ? amountStats.totalAmount || 0 : undefined,
    totalAmountWithSubscription: enabled ? amountStats.totalAmountWithSubscription || 0 : undefined,
    // show loading while a retryable error is mid-retry; a non-retryable error or exhausted retries are terminal
    isPending: isCalculatingStats || isLoading || (error && !hasExhaustedRetryCount && isRetryableError(error)),
    error: errorWithHasErrorAfterAllRetries,
  };
}
